import { useEffect, useRef, useCallback, useMemo } from 'react'
import { Skeleton, Spin, Typography, theme } from 'antd'
import { observer } from 'mobx-react-lite'
import { formatDayLabel } from '@/Ship/Helpers/fieldViews'
import { useStore } from '../../Stores'
import { Message } from '../../Contracts'
import MessageWrapper from '../Messages/MessageWrapper'
import TextMessage from '../Messages/TextMessage'
import PhotoMessage from '../Messages/PhotoMessage'
import VideoMessage from '../Messages/VideoMessage'
import FileMessage from '../Messages/FileMessage'

const { Text } = Typography
const SKELETON_ROW_COUNT = 7
const SCROLL_BOTTOM_THRESHOLD = 100

/** Renders the correct message content: files first, then text */
function MessageContent({ message }: { message: Message }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {message.files?.map((file, i) => {
        switch (file.type) {
          case 'photo':
            return <PhotoMessage key={i} file={file} />
          case 'video':
            return <VideoMessage key={i} file={file} />
          case 'file':
            return <FileMessage key={i} file={file} />
        }
      })}
      {message.content && <TextMessage message={message} />}
    </div>
  )
}

/** Check if two timestamps are on different dates */
function isDifferentDay(ts1: number, ts2: number): boolean {
  const d1 = new Date(ts1)
  const d2 = new Date(ts2)
  return (
    d1.getFullYear() !== d2.getFullYear() ||
    d1.getMonth() !== d2.getMonth() ||
    d1.getDate() !== d2.getDate()
  )
}

/** Seeded PRNG (mulberry32) for stable-per-mount randomness */
function seededRandom(seed: number) {
  return () => {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function generateSkeletonRows(count: number) {
  const rand = seededRandom(Date.now())
  const widths = ['30%', '35%', '40%', '45%', '50%', '55%', '60%', '65%', '70%']
  const nameWidths = [60, 75, 90, 100, 110]

  return Array.from({ length: count }, () => {
    const lineCount = Math.floor(rand() * 3) + 1
    return {
      align: rand() > 0.45 ? ('flex-start' as const) : ('flex-end' as const),
      nameW: nameWidths[Math.floor(rand() * nameWidths.length)],
      lines: Array.from({ length: lineCount }, () =>
        widths[Math.floor(rand() * widths.length)],
      ),
    }
  })
}

function MessageListSkeleton({ bgColor }: { bgColor: string }) {
  const rows = useMemo(() => generateSkeletonRows(SKELETON_ROW_COUNT), [])

  return (
    <div style={{ flex: 1, padding: '16px 16px', overflow: 'hidden' }}>
      {rows.map((row, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 8,
            marginBottom: 14,
            flexDirection: row.align === 'flex-end' ? 'row-reverse' : 'row',
          }}
        >
          <Skeleton.Avatar active size={36} />
          <div
            style={{
              maxWidth: '65%',
              background: bgColor,
              borderRadius: 12,
              padding: '10px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <Skeleton.Input active size="small" style={{ width: row.nameW, height: 12, minWidth: row.nameW }} />
            {row.lines.map((w, j) => (
              <Skeleton.Input
                key={j}
                active
                size="small"
                style={{ width: w, height: 14, minWidth: 100 }}
              />
            ))}
            <Skeleton.Input active size="small" style={{ width: 40, height: 10, minWidth: 40, alignSelf: 'flex-end' }} />
          </div>
        </div>
      ))}
    </div>
  )
}

const MessageList = observer(() => {
  const { uiStore, messageStore, userStore } = useStore()
  const { token } = theme.useToken()
  const containerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isInitialScroll = useRef(true)
  const prevChatId = useRef<string | null>(null)
  const prevLastMsgId = useRef<string | null>(null)
  /**
   * «Липке дно»: чи був юзер біля дна на момент ОСТАННЬОГО скролу.
   * Перевіряти в момент load картинки вже пізно — вона щойно розтягнула
   * контент і відстань до дна стала висотою цієї ж картинки.
   */
  const wasAtBottomRef = useRef(true)

  const chatId = uiStore.activeChatId
  const messages = messageStore.activeChatMessages
  const isLoadingHistory = messageStore.isLoadingHistory
  const isLoading = messageStore.isLoading

  // Load messages when chat changes (skip if already loaded — e.g. re-mount after search panel).
  // "Loaded" comes from the store, NOT from messages.length: a single
  // realtime-arrived message must not suppress fetching the history.
  useEffect(() => {
    if (!chatId) return
    if (chatId !== prevChatId.current) {
      prevChatId.current = chatId
      isInitialScroll.current = true
      if (!messageStore.isChatLoaded(chatId)) {
        messageStore.loadMessages(chatId)
      }
    }
  }, [chatId, messageStore])

  // Handle pending scrollToMessageId (set by search panel)
  useEffect(() => {
    const targetId = uiStore.scrollToMessageId
    if (!targetId || messages.length === 0 || !chatId) return

    // Clear immediately: the effect re-runs on messages.length while
    // ensureMessageVisible pulls pages, and must not start a second loop
    uiStore.setScrollToMessage(null)
    isInitialScroll.current = false

    void (async () => {
      // Search hits may be beyond the loaded window — fetch older pages first
      await messageStore.ensureMessageVisible(chatId, targetId)

      // Double rAF: first waits for React re-render, second for DOM paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.getElementById(`message-${targetId}`)
          if (!el) return
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          el.classList.add('message-highlight')
          setTimeout(() => el.classList.remove('message-highlight'), 1500)
        })
      })
    })()
  }, [uiStore.scrollToMessageId, messages.length, uiStore, chatId, messageStore])

  // Auto-scroll to bottom on initial load or new message
  useEffect(() => {
    const container = containerRef.current
    if (!container || messages.length === 0) return

    // Skip auto-scroll-to-bottom if we have a pending scroll target
    if (uiStore.scrollToMessageId) return

    if (isInitialScroll.current) {
      // Wait for DOM to render messages before scrolling
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight
        isInitialScroll.current = false
        // Одразу, не чекаючи scroll-події: картинка може встигнути
        // завантажитись раніше за неї
        wasAtBottomRef.current = true
      })
      prevLastMsgId.current = messages[messages.length - 1]?.id ?? null
      return
    }

    // Detect if the last visible message changed (new message appeared at bottom)
    const lastMsg = messages[messages.length - 1]
    const lastId = lastMsg?.id ?? null
    const isNewMessage = lastId !== prevLastMsgId.current
    prevLastMsgId.current = lastId

    if (!isNewMessage || !lastMsg) return

    // Always scroll for own messages; for others — only if near bottom
    const isMine = lastMsg.senderId === userStore.currentUserId

    const { scrollTop, scrollHeight, clientHeight } = container
    const isNearBottom = scrollHeight - scrollTop - clientHeight < SCROLL_BOTTOM_THRESHOLD

    if (isMine || isNearBottom) {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight
        wasAtBottomRef.current = true
      })
    }
  }, [messages.length, messages, uiStore.scrollToMessageId, userStore.currentUserId])

  // IntersectionObserver for lazy loading old messages
  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0]
      if (entry?.isIntersecting && chatId && !isLoadingHistory && !isInitialScroll.current) {
        messageStore.loadMoreMessages(chatId)
      }
    },
    [chatId, isLoadingHistory, messageStore],
  )

  // isLoading in deps is essential: while loading the component renders the
  // skeleton branch, so the container and sentinel are REPLACED by new DOM
  // nodes afterwards — the observer must re-attach to the new sentinel,
  // otherwise it keeps watching a detached node and pagination goes dead
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(observerCallback, {
      root: containerRef.current,
      threshold: 0.1,
    })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [observerCallback, isLoading])

  if (!chatId) return null

  // Пізнє розтягування контенту (типово: <img> вкладення завантажилось
  // ПІСЛЯ скролу на дно і зсунуло його) — докручуємо, якщо юзер був біля
  // дна. onLoadCapture на контейнері ловить load усіх дочірніх картинок:
  // подія load не спливає, але capture-фаза проходить згори вниз
  const handleTrackScroll = () => {
    const container = containerRef.current
    if (!container) return
    const { scrollTop, scrollHeight, clientHeight } = container
    wasAtBottomRef.current = scrollHeight - scrollTop - clientHeight < SCROLL_BOTTOM_THRESHOLD
  }

  const handleImageLoadCapture = () => {
    const container = containerRef.current
    if (!container || !wasAtBottomRef.current) return
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight
    })
  }

  if (isLoading) {
    return <MessageListSkeleton bgColor={token.colorFillTertiary} />
  }

  return (
    <div
      ref={containerRef}
      className="chat-scroll"
      onScroll={handleTrackScroll}
      onLoadCapture={handleImageLoadCapture}
      style={{
        flex: 1,
        overflowY: 'auto',
        paddingTop: 8,
        paddingBottom: 8,
      }}
    >
      {/* Highlight animation for scroll-to-reply */}
      <style>{`
        @keyframes messageHighlight {
          0% { background: transparent; }
          20% { background: color-mix(in srgb, var(--ant-color-primary) 12%, transparent); }
          100% { background: transparent; }
        }
        .message-highlight {
          animation: messageHighlight 1.5s ease-out;
        }
      `}</style>
      {/* Sentinel for infinite scroll (top) */}
      <div ref={sentinelRef} style={{ height: 1 }} />

      {/* Loading indicator for history */}
      {isLoadingHistory && (
        <div style={{ textAlign: 'center', padding: 12 }}>
          <Spin size="small" />
        </div>
      )}

      {messages.map((msg: Message, idx: number) => {
        const prev = idx > 0 ? messages[idx - 1] : null
        const next = idx < messages.length - 1 ? messages[idx + 1] : null
        const showDateSep = !prev || isDifferentDay(prev.timestamp, msg.timestamp)

        // Grouping: consecutive messages from the same sender (no date break)
        const prevSameSender = prev && prev.senderId === msg.senderId && !showDateSep
        const nextSameSender =
          next && next.senderId === msg.senderId && !isDifferentDay(msg.timestamp, next.timestamp)

        const isFirstInGroup = !prevSameSender
        const isLastInGroup = !nextSameSender

        return (
          <div key={msg.id} id={`message-${msg.id}`} className="chat-msg-appear">
            {/* Date separator */}
            {showDateSep && (
              <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
                <Text
                  style={{
                    fontSize: 12,
                    background: token.colorFillTertiary,
                    padding: '2px 12px',
                    borderRadius: 10,
                    color: token.colorTextSecondary,
                  }}
                >
                  {formatDayLabel(msg.timestamp)}
                </Text>
              </div>
            )}
            <MessageWrapper
              message={msg}
              isFirstInGroup={isFirstInGroup}
              isLastInGroup={isLastInGroup}
            >
              <MessageContent message={msg} />
            </MessageWrapper>
          </div>
        )
      })}
    </div>
  )
})

export default MessageList
